import { useEffect, useState } from "react";
import { Apple, ChevronDown, Download, Monitor } from "lucide-react";

import { desktopShell } from "@/lib/desktopBridge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// §14.11.3 The download button for the desktop shell.
//
// The installers are built on a Mac by `npm run desktop:release`, published as GitHub Release
// assets, and pulled into the image by the deploy job (§14.9). This component never hardcodes
// a version: it reads `/downloads/manifest.json`, so a release is a deploy and nothing else.
//
// Renders nothing in two cases, both deliberate:
//   * inside the desktop shell — offering the app to someone already running it is noise;
//   * the manifest is missing or unreadable (`npm run dev` with no release staged, or a deploy
//     that lost the assets) — a button that 404s is worse than no button.
//
// An unrecognised OS is NOT one of them. Detection is a guess, and a wrong guess with no
// escape hatch strands the user (§14.11.3), so a machine we cannot place still gets both
// builds in the menu — it just loses the one-click primary action.

interface ManifestFile {
  name: string;
  size: number;
}

interface DesktopManifest {
  version: string;
  releasedAt: string;
  files: { mac: ManifestFile; win: ManifestFile };
}

type Platform = "mac" | "win";

function isManifest(value: unknown): value is DesktopManifest {
  const m = value as DesktopManifest | null;
  return (
    !!m &&
    typeof m.version === "string" &&
    typeof m.files?.mac?.name === "string" &&
    typeof m.files?.win?.name === "string"
  );
}

// `navigator.platform` is deprecated but is still the only signal some browsers give, so it
// backs up `userAgentData`. Phones and tablets are excluded outright rather than guessed at:
// iPadOS reports "MacIntel", and offering a 226 MB .dmg to an iPad is a pure waste of a tap.
function detectPlatform(): Platform | null {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };
  const haystack =
    `${nav.userAgentData?.platform ?? ""} ${nav.platform ?? ""} ${nav.userAgent ?? ""}`.toLowerCase();

  if (/android|iphone|ipad|ipod/.test(haystack)) return null;
  // A touch-capable "MacIntel" is an iPad denying it is one.
  if (haystack.includes("mac")) {
    return navigator.maxTouchPoints > 1 ? null : "mac";
  }
  if (haystack.includes("win")) return "win";
  return null;
}

function megabytes(bytes: number): string {
  return `${Math.round(bytes / 1_000_000)} MB`;
}

export function DownloadDesktopApp() {
  const [manifest, setManifest] = useState<DesktopManifest | null>(null);
  const [platform] = useState<Platform | null>(detectPlatform);

  useEffect(() => {
    // The shell serves the same bundle as the site (§14.4.5), so this component is mounted
    // there too — it just has nothing to offer. Bail before the fetch.
    if (desktopShell()) return;

    let cancelled = false;
    void fetch("/downloads/manifest.json")
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled && isManifest(data)) setManifest(data);
      })
      // A 404 is the normal state in dev, not an error worth a console entry.
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  if (!manifest) return null;

  const href = (p: Platform) => `/downloads/${manifest.files[p].name}`;
  const label: Record<Platform, string> = { mac: "macOS", win: "Windows" };
  const Icon = platform === "win" ? Monitor : Apple;
  // With no detected platform the primary action would be a coin flip, so the whole control
  // becomes the menu instead — both builds, neither one privileged.
  const others: Platform[] = platform
    ? (["mac", "win"] as Platform[]).filter((p) => p !== platform)
    : ["mac", "win"];

  return (
    <div className="flex items-center">
      {platform && (
        <Button
          variant="secondary"
          size="sm"
          className="gap-2 rounded-r-none"
          asChild
        >
          <a href={href(platform)} download>
            <Download className="h-4 w-4" />
            Desktop app
          </a>
        </Button>
      )}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="secondary"
            size="sm"
            className={
              platform
                ? "rounded-l-none border-l border-slate-300 px-1.5"
                : "gap-2"
            }
            aria-label="Desktop app download options"
          >
            {!platform && <Download className="h-4 w-4" />}
            {!platform && "Desktop app"}
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">
              Work offline, and run AI locally
            </span>
            <span className="text-xs font-normal text-slate-500">
              Version {manifest.version}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {platform && (
            <DropdownMenuItem asChild>
              <a href={href(platform)} download>
                <Icon className="mr-2 h-4 w-4" />
                {label[platform]} · {megabytes(manifest.files[platform].size)}
              </a>
            </DropdownMenuItem>
          )}
          {others.map((p) => (
            <DropdownMenuItem key={p} asChild>
              <a href={href(p)} download>
                {p === "mac" ? (
                  <Apple className="mr-2 h-4 w-4" />
                ) : (
                  <Monitor className="mr-2 h-4 w-4" />
                )}
                {label[p]} · {megabytes(manifest.files[p].size)}
              </a>
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          {/* These builds are unsigned (§14.9/§14.10 are not done), and an unsigned installer
              with no instructions is a dead end for most people — so the instructions ship
              with the button rather than in a support page nobody reaches.

              On macOS the download quarantine invalidates the ad-hoc signature the build
              carries, and the app reports itself as "damaged". Right-click → Open is NOT
              enough on current macOS for an ad-hoc signature; the attribute has to come off.
              Remove these lines the day a Developer ID signs the build. */}
          <div className="px-2 py-1.5 text-xs text-slate-500">
            <p className="font-medium text-slate-600">
              Opening it the first time
            </p>
            <p className="mt-1">
              These builds aren’t signed yet, so your OS will object once.
            </p>
            <p className="mt-1">
              <span className="font-medium">macOS:</span> if it says the app is
              damaged, run{" "}
              <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">
                xattr -dr com.apple.quarantine &quot;/Applications/SlideCraft
                Studio.app&quot;
              </code>
            </p>
            <p className="mt-1">
              <span className="font-medium">Windows:</span> on the SmartScreen
              prompt, choose <span className="font-medium">More info</span> →{" "}
              <span className="font-medium">Run anyway</span>.
            </p>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
